// src/components/MaintenanceNotice.jsx
// Displays offline notice during 3-6 AM when bot is off
// Admins can bypass by checking their status

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import './MaintenanceNotice.css';

function MaintenanceNotice() {
  const [isOffline, setIsOffline] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0 });
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    checkUserStatus();
    checkOfflineStatus();
    // Check every minute
    const interval = setInterval(checkOfflineStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  async function checkUserStatus() {
    try {
      const data = await api.get('/api-me');
      setIsAdmin(data.isAdmin || false);
    } catch (error) {
      // User not logged in or error - show maintenance if needed
      setIsAdmin(false);
    } finally {
      setCheckingAuth(false);
    }
  }

  function checkOfflineStatus() {
    const now = new Date();
    const hour = now.getHours();
    // Offline between 3:00 and 6:00 (24h format)
    const offline = hour >= 3 && hour < 6;
    setIsOffline(offline);
    
    if (offline) {
      const endTime = new Date(now);
      endTime.setHours(6, 0, 0, 0);
      const minutesLeft = Math.round((endTime - now) / 60000);
      setTimeLeft({
        hours: Math.floor(minutesLeft / 60),
        minutes: minutesLeft % 60
      });
    }
  }

  // Don't show anything while checking auth
  if (checkingAuth) {
    return null;
  }

  // Admins bypass maintenance
  if (isAdmin) {
    return null;
  }

  if (!isOffline) {
    return null;
  }

  return (
    <div className="offline-overlay">
      <div className="offline-card">
        {/* Decorative elements */}
        <div className="offline-card__glow"></div>
        <div className="offline-card__border"></div>
        
        {/* Icon */}
        <div className="offline-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="url(#offline-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <defs>
              <linearGradient id="offline-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>
            </defs>
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
        </div>
        
        {/* Content */}
        <h1 className="offline-title">Strona Niedostępna</h1>
        <p className="offline-time">03:00 - 06:00</p>
        
        <div className="offline-content">
          <p>System MP jest wyłączony w godzinach nocnych.</p>
          <p>Wrócimy o <strong>6:00</strong> z pełną funkcjonalnością.</p>
        </div>

        {/* Countdown */}
        <div className="offline-countdown">
          <span className="offline-countdown__label">Wracamy za</span>
          <div className="offline-countdown__time">
            <div className="offline-countdown__unit">
              <span className="offline-countdown__value">{timeLeft.hours}</span>
              <span className="offline-countdown__text">godz</span>
            </div>
            <span className="offline-countdown__separator">:</span>
            <div className="offline-countdown__unit">
              <span className="offline-countdown__value">{String(timeLeft.minutes).padStart(2, '0')}</span>
              <span className="offline-countdown__text">min</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="offline-footer">
          <p>Przepraszamy za niedogodności</p>
          <p className="offline-team">— Zespół Military Police</p>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceNotice;
